import {
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MaterialIcons,
  MenuItem,
  Select,
  Typography,
} from '@usehatchapp/material';
import React, { FC } from 'react';

import Interval from './Interval';

const { Add } = MaterialIcons;

export type BusinessHours = {
  open: { day: number; hour: number; minute: number };
  close: { day: number; hour: number; minute: number };
};

type IntervalListProps = {
  mappedHoursToDays: Record<string, BusinessHours[]>;
  onChangeHours: (args: {
    day: number;
    hour: number;
    index: number;
    kind: string;
    minute: number;
  }) => void;
  setHours: (hours: BusinessHours[]) => void;
  hours: BusinessHours[];
  editing: boolean;
  removeInterval: (interval: BusinessHours) => void;
};

export const DAYS_OF_WEEK = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];
const OPTIONS = ['Open', 'Closed'];

export const initialHoursToDays = {};
export const initialHours = [];
DAYS_OF_WEEK.forEach((day, index) => {
  const interval = {
    close: { day: index, hour: 17, minute: 0 },
    open: { day: index, hour: 9, minute: 0 },
  };
  initialHoursToDays[day] = [interval];
  initialHours.push(interval);
});

// This strips any hour intervals with a certain day
export const setDayToClosedHours = ({
  index,
  hours,
}: {
  index: number;
  hours: BusinessHours[];
}): BusinessHours[] => {
  return hours.filter((interval) => interval?.open?.day !== index);
};

// This strips any day interval and then resets that day to the default interval
const setDayToDefaultHours = ({ index, hours }) => {
  return [
    ...setDayToClosedHours({ hours, index }),
    {
      close: { day: index, hour: 17, minute: 0 },
      open: { day: index, hour: 9, minute: 0 },
    },
  ];
};

const addAdditionalInterval = ({ day, hours, setHours }) => {
  const dayIndex = DAYS_OF_WEEK.indexOf(day);
  const nextBusinessHours = [
    ...hours,
    {
      close: { day: dayIndex, hour: 17, minute: 0 },
      open: { day: dayIndex, hour: 9, minute: 0 },
    },
  ];
  setHours(nextBusinessHours);
};

export const IntervalList: FC<IntervalListProps> = ({
  mappedHoursToDays,
  onChangeHours,
  setHours,
  hours,
  editing,
  removeInterval,
}) => {
  return (
    <>
      {DAYS_OF_WEEK.map((day, index) => {
        const onChangeOpen = ({ target: { value } }) => {
          if (value === 'Open') {
            setHours(setDayToDefaultHours({ hours, index }));
          } else {
            setHours(setDayToClosedHours({ hours, index }));
          }
        };
        // If there are some set hours
        const isOpen = mappedHoursToDays[day]?.length;

        return (
          <Grid key={day} container flexWrap="nowrap" mt={2} width="100%">
            {editing ? (
              <FormControl size="small" sx={{ minWidth: '175px' }}>
                <InputLabel htmlFor={`${day}-select`}>{day}</InputLabel>
                <Select
                  size="small"
                  data-test-id="ecf005b0-d577-43d5-aa03-3f5266eb81b1"
                  onChange={onChangeOpen}
                  label={day}
                  id={`${day}-select`}
                  value={isOpen ? OPTIONS[0] : OPTIONS[1]}
                  disabled={!editing}
                >
                  {OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Typography sx={{ minWidth: '100px' }}>{day}</Typography>
            )}
            <Grid container alignItems="center">
              {isOpen ? (
                <>
                  <Grid container flexDirection="column" sx={{ width: 'auto' }}>
                    {mappedHoursToDays[day].map(({ open, close }, i) => {
                      return (
                        <Interval
                          index={i}
                          onChangeHours={onChangeHours}
                          day={day}
                          open={open}
                          close={close}
                          key={`${day}-${i}`} // eslint-disable-line
                          editing={editing}
                          removeInterval={removeInterval}
                        />
                      );
                    })}
                  </Grid>
                  {editing ? (
                    <IconButton
                      sx={{ ml: 1 }}
                      data-test-id="21476fda-9bc2-49f4-bb35-48a9f4bf573d"
                      onClick={() => {
                        addAdditionalInterval({ day, hours, setHours });
                      }}
                    >
                      <Add color={editing ? 'secondary' : 'disabled'} />
                    </IconButton>
                  ) : null}
                </>
              ) : null}
              {!editing && !isOpen ? <Typography>Closed</Typography> : null}
            </Grid>
          </Grid>
        );
      })}
    </>
  );
};
